import { useEffect, useState } from 'react';
import type { AuthorityRow, InlineAuthorityService } from '@shared/types/api';
import * as admin from '@shared/api/admin';

interface AuthorityModalProps {
  editingAuth: AuthorityRow | null;
  isOpen: boolean;
  isSaving: boolean;
  readOnly?: boolean;
  onClose: () => void;
  onSave: (data: { name: string; code?: string | null; images?: string[]; services?: InlineAuthorityService[] }) => Promise<void>;
}

export function AuthorityModal({
  editingAuth,
  isOpen,
  isSaving,
  readOnly = false,
  onClose,
  onSave,
}: AuthorityModalProps) {
  const [authName, setAuthName] = useState(editingAuth ? editingAuth.name : '');
  const [authCode, setAuthCode] = useState(editingAuth ? editingAuth.code || '' : '');
  const [images, setImages] = useState<string[]>(editingAuth?.images ?? []);
  const [previewUrl, setPreviewUrl] = useState<string | null>(editingAuth?.images?.[0] || null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [inlineServices, setInlineServices] = useState<InlineAuthorityService[]>(
    editingAuth ? [] : [{ code: '', name: '' }],
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setAuthName(editingAuth ? editingAuth.name : '');
      setAuthCode(editingAuth ? editingAuth.code || '' : '');
      setImages(editingAuth?.images ?? []);
      setPreviewUrl(editingAuth?.images?.[0] || null);
      setUploadError(null);
      setInlineServices(editingAuth ? [] : [{ code: '', name: '' }]);
      setErrorMsg(null);
    }
  }, [isOpen, editingAuth]);

  if (!isOpen) return null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      const key = await admin.uploadServiceImage(file);
      setImages([key]);
      setPreviewUrl(URL.createObjectURL(file));
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImages([]);
    setPreviewUrl(null);
  };

  const handleAddInlineServiceRow = () => {
    setInlineServices((prev) => [...prev, { code: '', name: '' }]);
  };

  const handleRemoveInlineServiceRow = (index: number) => {
    setInlineServices((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateInlineServiceRow = (index: number, field: 'code' | 'name', value: string) => {
    setInlineServices((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authName.trim()) return;

    setErrorMsg(null);
    const validServices = inlineServices.filter(
      (s) => s.code.trim() !== '' && s.name.trim() !== '',
    );

    try {
      await onSave({
        name: authName.trim(),
        code: authCode.trim() || null,
        images,
        services: editingAuth ? undefined : validServices,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to save authority';
      setErrorMsg(detail);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        {/* Fixed Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 bg-white z-10 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {readOnly ? `View Authority: ${editingAuth?.name || ''}` : editingAuth ? 'Edit Authority' : 'Add Authority & Services'}
            </h3>
            <p className="text-xs text-gray-500">
              {readOnly ? 'Authority details.' : editingAuth ? 'Update authority details.' : 'Create authority and optionally add its services below.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form Container */}
        <form onSubmit={readOnly ? (e) => { e.preventDefault(); onClose(); } : handleSubmit} className="flex flex-col flex-1 min-h-0">
          <fieldset disabled={readOnly} className="flex-1 overflow-y-auto p-6 space-y-5">
            {errorMsg && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 font-medium flex items-center justify-between">
                <span>{errorMsg}</span>
                <button type="button" onClick={() => setErrorMsg(null)} className="text-red-500 hover:text-red-700 font-bold ml-2">×</button>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Authority Name *
              </label>
              <input
                type="text"
                required
                value={authName}
                onChange={(e) => setAuthName(e.target.value)}
                placeholder="e.g. MINISTRY OF CORPORATE AFFAIRS (MCA)"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Authority Code (Short ID / Tag)
              </label>
              <input
                type="text"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                placeholder="e.g. MCA"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                Authority Image
              </label>
              <div className="mt-1 flex items-center gap-3">
                {previewUrl ? (
                  <div className="relative h-20 w-28 border border-gray-200 rounded-lg overflow-hidden bg-white p-1 shrink-0 flex items-center justify-center">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="h-full w-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 text-white font-bold opacity-0 hover:opacity-100 transition-opacity"
                      title="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="flex h-20 w-28 items-center justify-center border border-dashed border-gray-300 rounded-lg bg-gray-50 text-[10px] text-gray-400 shrink-0">
                    No Image
                  </div>
                )}

                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                    onChange={handleFileChange}
                    className="block w-full text-xs text-gray-500 file:mr-3 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 file:cursor-pointer hover:file:bg-brand-100"
                  />
                  <p className="mt-1 text-[10px] text-gray-400">
                    JPG, PNG, WEBP, or SVG. Max 15MB.
                  </p>
                  {uploading && <p className="text-[11px] text-brand-600 font-medium">Uploading to S3...</p>}
                  {uploadError && <p className="text-[11px] text-red-600 font-medium">{uploadError}</p>}
                </div>
              </div>
            </div>

            {/* Inline Services Section (Only when creating a new authority) */}
            {!editingAuth && (
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Services under this Authority
                  </label>
                  <button
                    type="button"
                    onClick={handleAddInlineServiceRow}
                    className="text-xs font-semibold text-brand-700 hover:text-brand-800 flex items-center gap-1"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Another Service
                  </button>
                </div>

                <div className="space-y-3">
                  {inlineServices.map((svc, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                      <div className="w-32">
                        <input
                          type="text"
                          value={svc.code}
                          onChange={(e) => handleUpdateInlineServiceRow(idx, 'code', e.target.value)}
                          placeholder="ID (e.g. MCA-001)"
                          className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-xs text-gray-900 font-mono shadow-sm focus:border-brand-500 focus:outline-none"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={svc.name}
                          onChange={(e) => handleUpdateInlineServiceRow(idx, 'name', e.target.value)}
                          placeholder="Service Name (e.g. Name Reservation)"
                          className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-xs text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none"
                        />
                      </div>
                      {inlineServices.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveInlineServiceRow(idx)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded shrink-0"
                          title="Remove row"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </fieldset>

          {/* Permanently Fixed Bottom Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 z-10 shrink-0">
            {readOnly ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Authority'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
